import { useEffect, useState } from "react";
import * as m from "~/paraglide/messages.js";

/** Below this the page is short enough that the bar is still a scroll away. */
const REVEAL_AT = 300;

/**
 * The back-to-top control: a quiet pill that sits under the sticky bar.
 * Layout and colour live in `styles/lindevo/chrome.css` as `.to-top`.
 *
 * Deliberately free of framer-motion. The predecessor sprang in on a spring
 * from `y: -20`; the system has one transition and one keyframe, and the
 * keyframe answers a click, so the control simply appears — the same way the
 * mobile nav panel does.
 *
 * It is toggled with `hidden` rather than unmounted, so the button exists
 * before it is needed and `base.css`'s `[hidden]` rule does the hiding.
 */
export function ScrollToTop() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const sync = () => setIsVisible(window.scrollY > REVEAL_AT);
    // Read once: a restored scroll position fires no event of its own.
    sync();
    window.addEventListener("scroll", sync, { passive: true });
    return () => window.removeEventListener("scroll", sync);
  }, []);

  return (
    <button
      aria-label={m.nav_to_top_label()}
      className="to-top"
      hidden={!isVisible}
      onClick={() => {
        // No `behavior` key on purpose. `html { scroll-behavior: smooth }` in
        // base.css supplies the easing and flips to `auto` under
        // prefers-reduced-motion; naming "smooth" here would override both.
        window.scrollTo({ top: 0 });
        // The button hides itself the moment the page clears the threshold, so
        // keyboard focus would land on <body>. Hand it to the wordmark instead,
        // which is where the document starts.
        //
        // `preventScroll` is not optional: focus() otherwise runs its
        // scroll-into-view step, and that cancels the smooth scroll started a
        // line above — the page stopped dead partway up.
        document
          .querySelector<HTMLAnchorElement>(".site-header .logo")
          ?.focus({ preventScroll: true });
      }}
      type="button"
    >
      {m.nav_to_top()} <span aria-hidden="true">↑</span>
    </button>
  );
}
